import { useSubmitEnrollmentJointConfirmInformationData } from '@egw/api'
import { EmploymentStatus } from '@egw/api/src/models/enrollment/enrollment'
import { EnrollmentForm, ModalDrawer, type EnrollmentFormField, type FieldValues } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type FC, useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { employmentStatusItems, occupationItems } from '../../constants/confirm-personal-info/confirm-personal-info'
import type { EditEmploymentModalProps, EmploymentFormData } from './edit-employment-modal.types'

const log = new Logger('Enrollment - Joint - EditEmploymentModal')

export const EditEmploymentModal: FC<EditEmploymentModalProps> = ({
  isOpen,
  onClose,
  currentEmploymentStatus,
  currentOccupation,
  onSuccess,
}) => {
  const { t } = useTranslation('features-enrollment')

  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentJointConfirmInformationData()
  const [submitError, setSubmitError] = useState<string | undefined>()

  // Local state to track the updated values to prevent brief revert
  const [localEmploymentStatus, setLocalEmploymentStatus] = useState<string>(currentEmploymentStatus ?? '')
  const [localOccupation, setLocalOccupation] = useState<string>(currentOccupation ?? '')

  // Track whether occupation field should be disabled
  const [isOccupationDisabled, setIsOccupationDisabled] = useState<boolean>(
    !(
      currentEmploymentStatus === EmploymentStatus.Employed || currentEmploymentStatus === EmploymentStatus.Independent
    ),
  )

  // Track form values to properly clear occupation when needed
  const formValuesRef = useRef<FieldValues>({
    employmentStatus: currentEmploymentStatus ?? '',
    occupation: currentOccupation ?? '',
  })

  // Update local state when modal reopens
  useEffect(() => {
    if (isOpen) {
      setLocalEmploymentStatus(currentEmploymentStatus ?? '')
      setLocalOccupation(currentOccupation ?? '')
      setIsOccupationDisabled(
        !(
          currentEmploymentStatus === EmploymentStatus.Employed ||
          currentEmploymentStatus === EmploymentStatus.Independent
        ),
      )
      formValuesRef.current = {
        employmentStatus: currentEmploymentStatus ?? '',
        occupation: currentOccupation ?? '',
      }
      setSubmitError(undefined)
    }
  }, [isOpen, currentEmploymentStatus, currentOccupation])

  // Define form fields - occupation field is always present but conditionally disabled
  const fields: EnrollmentFormField[][] = useMemo(() => {
    return [
      [
        {
          name: 'employmentStatus',
          type: 'radio',
          label: t('joint.employment.employment_status_title'),
          required: true,
          testId: 'edit-employmentStatus-radio',
          options: employmentStatusItems.map((item) => ({
            value: item.value,
            label: t(`joint.employment.employment_status.${item.value}`),
          })),
        },
      ],
      [
        {
          name: 'occupation',
          type: 'radio',
          label: t('joint.employment.occupation_title'),
          required: !isOccupationDisabled,
          disabled: isOccupationDisabled,
          testId: 'edit-occupation-radio',
          options: occupationItems.map((item) => ({
            value: item.value,
            label: t(`joint.employment.occupation.${item.value}`),
          })),
        },
      ],
    ]
  }, [t, isOccupationDisabled])

  // Memoize default values to prevent unnecessary form resets
  const defaultFormValues = useMemo(
    () => ({
      employmentStatus: localEmploymentStatus,
      occupation: localOccupation,
    }),
    [localEmploymentStatus, localOccupation],
  )

  // Handle form value changes to enable/disable occupation field
  const handleValuesChange = useCallback((values: FieldValues) => {
    const employmentStatus = values.employmentStatus as EmploymentStatus
    const shouldDisableOccupation = !(
      employmentStatus === EmploymentStatus.Employed || employmentStatus === EmploymentStatus.Independent
    )

    // Update local state to keep it in sync with form values
    setLocalEmploymentStatus(employmentStatus)

    // Update form values ref
    formValuesRef.current = { ...values }

    // Update disabled state and clear occupation if needed
    if (shouldDisableOccupation) {
      setIsOccupationDisabled(true)
      if (values.occupation) {
        formValuesRef.current.occupation = ''
        setLocalOccupation('')
      }
    } else {
      setIsOccupationDisabled(false)
      setLocalOccupation(values.occupation as string)
    }
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: FieldValues): Promise<void> => {
      // Clear any previous errors
      setSubmitError(undefined)

      // Cast to our specific form data type
      const data = formData as EmploymentFormData

      // Update local state immediately to prevent revert during submission
      setLocalEmploymentStatus(data.employmentStatus)
      setLocalOccupation(data.occupation)

      try {
        log.info('Submitting employment data update')

        // If changing to unemployed/retired/student, clear occupation
        const shouldClearOccupation = [
          EmploymentStatus.Unemployed,
          EmploymentStatus.Retired,
          EmploymentStatus.Student,
        ].includes(data.employmentStatus as EmploymentStatus)

        const response = await mutate({
          activePerson: {
            employmentStatus: data.employmentStatus,
            occupation: shouldClearOccupation ? undefined : data.occupation,
          },
        })

        // Check for API errors
        if (response?.isError) {
          log.error('Error updating employment data', response.errors)
          setSubmitError(t('common.generic_error'))

          // Revert local state on API error
          setLocalEmploymentStatus(currentEmploymentStatus ?? '')
          setLocalOccupation(currentOccupation ?? '')
          return
        }

        log.info('Employment data updated successfully')

        // Call success callback with the updated values
        if (onSuccess) {
          await Promise.resolve(onSuccess(data.employmentStatus, shouldClearOccupation ? undefined : data.occupation))
        }

        // Close modal
        onClose()
      } catch (error) {
        log.error('Error submitting employment data update', error)
        setSubmitError(t('common.generic_error'))

        // Revert local state on error
        setLocalEmploymentStatus(currentEmploymentStatus ?? '')
        setLocalOccupation(currentOccupation ?? '')
      }
    },
    [mutate, onSuccess, onClose, t, currentEmploymentStatus, currentOccupation],
  )

  return (
    <ModalDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('joint.confirm_information.edit_modals.edit_employment_title')}
      testId="edit-employment-modal"
    >
      <EnrollmentForm
        fields={fields}
        defaultValues={defaultFormValues}
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        submitButtonText={t('common.save')}
        isSubmitting={isSubmitting}
        submitError={submitError}
        mode="onChange"
        testId="edit-employment-form"
      />
    </ModalDrawer>
  )
}

export type { EditEmploymentModalProps }
